import React from "react";
import { createPortal } from "react-dom";
import Swal from "sweetalert2";

import { emergencyAlertsApi } from "../lib/api/handlers/emergencyAlerts";
import { ApiError } from "../lib/api/http";

const EMERGENCY_OPTIONS = [
  {
    id: "suspected-infectious-disease",
    label: "Suspected infectious disease",
    description: "Individual with symptoms matching a notifiable disease.",
  },
  {
    id: "mass-casualty",
    label: "Mass casualty event",
    description: "Large number of affected travellers requiring triage.",
  },
  {
    id: "hazardous-material",
    label: "Hazardous material exposure",
    description: "Chemical, biological, or radiological incident on site.",
  },
  {
    id: "vector-outbreak",
    label: "Vector control breach",
    description: "Mosquito or rodent infestation escalating beyond control levels.",
  },
  {
    id: "infrastructure-failure",
    label: "Critical infrastructure failure",
    description: "Power, water, or ventilation disruption impacting operations.",
  },
  {
    id: "security-threat",
    label: "Security or safety threat",
    description: "Violence, tampering, or hostile activity in port health areas.",
  },
] as const;

type EmergencyAlertModalProps = {
  open: boolean;
  onClose: () => void;
};

type EmergencyOptionId = (typeof EMERGENCY_OPTIONS)[number]["id"];

export function EmergencyAlertModal({ open, onClose }: EmergencyAlertModalProps) {
  const [selectedOptions, setSelectedOptions] = React.useState<EmergencyOptionId[]>([]);
  const [comment, setComment] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const toggleOption = React.useCallback((optionId: EmergencyOptionId) => {
    setSelectedOptions((prev) =>
      prev.includes(optionId)
        ? prev.filter((id) => id !== optionId)
        : [...prev, optionId],
    );
  }, []);

  const closeModal = React.useCallback(
    (force = false) => {
      if (submitting && !force) return;
      onClose();
    },
    [onClose, submitting],
  );

  const handleSubmit = React.useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!selectedOptions.length && !comment.trim()) {
        setError("Select at least one emergency type or provide a comment.");
        return;
      }

      setSubmitting(true);
      setError(null);
      try {
        const result = await emergencyAlertsApi.create({
          categories: selectedOptions,
          comment: comment.trim() || undefined,
          triggeredAt: new Date().toISOString(),
        });
        setSelectedOptions([]);
        setComment("");
        closeModal(true);
        const pending = Boolean(result?.pendingAuthorization);
        await Swal.fire({
          icon: pending ? "info" : "success",
          title: pending ? "Awaiting approval" : "Alert sent",
          text: pending
            ? "Alert logged and awaiting supervisor approval before notifications are issued."
            : "Emergency operations team has been notified.",
          confirmButtonColor: "#2563eb",
        });
      } catch (cause) {
        const message =
          cause instanceof ApiError
            ? cause.message || "Could not send the emergency alert."
            : "Could not send the emergency alert.";
        setError(message);
      } finally {
        setSubmitting(false);
      }
    },
    [closeModal, comment, selectedOptions],
  );

  React.useEffect(() => {
    if (!open) {
      setSelectedOptions([]);
      setComment("");
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  if (!open) {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center overflow-y-auto bg-slate-900/70 px-4 py-8"
      onClick={() => closeModal()}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="flex w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl max-h-[90vh]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Rapid emergency alert</h2>
            <p className="mt-1 text-sm text-slate-500">
              Notify command to dispatch the appropriate response teams.
            </p>
          </div>
          <button
            type="button"
            onClick={() => closeModal()}
            disabled={submitting}
            className="rounded-lg border border-slate-200 px-3 py-1 text-sm text-slate-600 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Close
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-1 min-h-0 flex-col">
          <div className="flex-1 min-h-0 space-y-6 overflow-y-auto px-6 py-5">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Emergency types
              </span>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                {EMERGENCY_OPTIONS.map((option) => {
                  const checked = selectedOptions.includes(option.id);
                  return (
                    <label
                      key={option.id}
                      className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 shadow-sm transition-colors ${
                        checked
                          ? "border-red-500 bg-red-50"
                          : "border-slate-200 bg-white hover:border-red-200"
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
                        checked={checked}
                        onChange={() => toggleOption(option.id)}
                      />
                      <span>
                        <span className="block text-sm font-semibold text-slate-900">{option.label}</span>
                        <span className="mt-1 block text-xs text-slate-500">{option.description}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="emergency-comment">
                Additional details
              </label>
              <textarea
                id="emergency-comment"
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                placeholder="Add instructions, location, or contacts for the response team."
                rows={4}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-800 transition focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-100"
              />
              <p className="mt-1 text-xs text-slate-400">Comment is optional but helps clarify the situation.</p>
            </div>

            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-3 border-t border-slate-200 px-6 py-4 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => closeModal()}
              disabled={submitting}
              className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center justify-center rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-100 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {submitting ? "Sending alert…" : "Send alert"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

export default EmergencyAlertModal;
